import PropTypes from 'prop-types';
import { Paperclip, Plus, RefreshCw, Search } from 'react-feather';
import Avatar from './Avatar';
import EstadoBadge from './EstadoBadge';
import PrioridadTag from './PrioridadTag';
import SlaBadge from './SlaBadge';
import TicketsAgenteFilter from './TicketsAgenteFilter';
import TicketsEstadoFilter from './TicketsEstadoFilter';
import { nombreUsuario, tiempoRelativo, slaEstado } from '../../features/tickets';

const TicketsList = ({
  titulo,
  lista,
  usuarios,
  isLoading,
  isError,
  isRefreshing,
  search,
  estados,
  estadosFiltro,
  onEstadosChange,
  agentesFiltro,
  onAgentesChange,
  onSearchChange,
  onPageChange,
  onOpen,
  onNew,
  onRefresh,
}) => {
  const { data = [], total = 0, page = 1, pageSize = 25 } = lista || {};
  const totalPaginas = Math.max(1, Math.ceil(total / pageSize));

  return (
    <section className="tickets-panel">
      <header className="tickets-listhead">
        <div>
          <h1 className="tickets-listhead__title">{titulo}</h1>
          <p className="tickets-listhead__sub">
            {total} {total === 1 ? 'ticket' : 'tickets'}
          </p>
        </div>
        <div className="tickets-listhead__actions">
          {estados.length > 0 && (
            <TicketsEstadoFilter
              estados={estados}
              seleccionados={estadosFiltro}
              onChange={onEstadosChange}
            />
          )}
          {usuarios.length > 0 && (
            <TicketsAgenteFilter
              usuarios={usuarios}
              seleccionados={agentesFiltro}
              onChange={onAgentesChange}
            />
          )}
          <span className="tickets-search">
            <Search size={15} aria-hidden="true" />
            <input
              type="search"
              className="tickets-search__input"
              placeholder="Buscar por nº, asunto o solicitante…"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              aria-label="Buscar tickets"
            />
          </span>
          <button
            type="button"
            className="tickets-btn tickets-btn--secondary tickets-btn--icon"
            onClick={onRefresh}
            disabled={isRefreshing}
            aria-label="Refrescar tickets"
            title="Refrescar"
          >
            <RefreshCw
              size={16}
              aria-hidden="true"
              className={isRefreshing ? 'tickets-spin' : ''}
            />
          </button>
          <button type="button" className="tickets-btn tickets-btn--primary" onClick={onNew}>
            <Plus size={16} aria-hidden="true" />
            Nuevo ticket
          </button>
        </div>
      </header>

      {isError ? (
        <div className="tickets-empty">No se pudieron cargar los tickets.</div>
      ) : isLoading ? (
        <div className="tickets-empty">Cargando tickets…</div>
      ) : data.length === 0 ? (
        <div className="tickets-empty">No hay tickets en esta vista.</div>
      ) : (
        <>
          <div className="tickets-tablewrap">
            <table className="tickets-table">
              <thead>
                <tr>
                  <th scope="col">Asunto</th>
                  <th scope="col">Estado</th>
                  <th scope="col">Prioridad</th>
                  <th scope="col">Agente</th>
                  <th scope="col">Edad</th>
                </tr>
              </thead>
              <tbody>
                {data.map((t) => {
                  const agente = nombreUsuario(usuarios, t.agente);
                  const sla = slaEstado(t);
                  return (
                    <tr
                      key={t.id}
                      className={`tickets-row${sla ? ` tickets-row--sla-${sla.nivel}` : ''}`}
                      onClick={() => onOpen(t.id)}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onOpen(t.id);
                        }
                      }}
                    >
                      <td>
                        <div className="tickets-row__asunto">
                          <span className="tickets-row__id">#{t.id}</span>
                          {t.asunto}
                          {t.num_adjuntos > 0 && (
                            <span
                              className="tickets-row__adjuntos"
                              title={`${t.num_adjuntos} ${
                                t.num_adjuntos === 1 ? 'adjunto' : 'adjuntos'
                              }`}
                            >
                              <Paperclip size={13} aria-hidden="true" />
                              <span className="visually-hidden">
                                {t.num_adjuntos} {t.num_adjuntos === 1 ? 'adjunto' : 'adjuntos'}
                              </span>
                            </span>
                          )}
                        </div>
                        <div className="tickets-row__meta">
                          {t.solicitante_nombre || t.solicitante_email} · {t.tema}
                        </div>
                      </td>
                      <td>
                        <EstadoBadge estadoId={t.estado} />
                      </td>
                      <td>
                        <PrioridadTag prioridad={t.prioridad} />
                      </td>
                      <td>
                        {agente ? (
                          <span className="tickets-agente">
                            <Avatar nombre={agente} size="sm" />
                            {agente}
                          </span>
                        ) : (
                          <span className="tickets-agente tickets-agente--empty">
                            <span
                              className="tickets-avatar tickets-avatar--sm tickets-avatar--empty"
                              aria-hidden="true"
                            />
                            sin asignar
                          </span>
                        )}
                      </td>
                      <td className="tickets-row__edad">
                        <div className="tickets-row__edad-inner">
                          <span className="tickets-row__edad-rel">
                            {tiempoRelativo(t.actualizado_en)}
                          </span>
                          <SlaBadge sla={sla} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPaginas > 1 && (
            <div className="tickets-pagination">
              <button
                type="button"
                className="tickets-btn tickets-btn--ghost"
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
              >
                Anterior
              </button>
              <span className="tickets-pagination__info">
                Página {page} de {totalPaginas}
              </span>
              <button
                type="button"
                className="tickets-btn tickets-btn--ghost"
                disabled={page >= totalPaginas}
                onClick={() => onPageChange(page + 1)}
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

TicketsList.propTypes = {
  titulo: PropTypes.string.isRequired,
  lista: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.object),
    total: PropTypes.number,
    page: PropTypes.number,
    pageSize: PropTypes.number,
  }),
  usuarios: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  isRefreshing: PropTypes.bool,
  search: PropTypes.string,
  estados: PropTypes.arrayOf(PropTypes.object),
  estadosFiltro: PropTypes.arrayOf(PropTypes.string),
  onEstadosChange: PropTypes.func,
  agentesFiltro: PropTypes.arrayOf(PropTypes.string),
  onAgentesChange: PropTypes.func,
  onSearchChange: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onNew: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
};

TicketsList.defaultProps = {
  lista: { data: [], total: 0, page: 1, pageSize: 25 },
  usuarios: [],
  isLoading: false,
  isError: false,
  isRefreshing: false,
  search: '',
  estados: [],
  estadosFiltro: [],
  onEstadosChange: () => {},
  agentesFiltro: [],
  onAgentesChange: () => {},
};

export default TicketsList;
